import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCopy, useFramework } from "../framework";
import {
  ArrowLinkLabel,
  EmptyState,
  PageHeader,
  SectionHeading,
  humanize,
  ordinal,
} from "../components/ui";
import { PlaceTrail } from "../components/PlaceTrail";
import { WorkMomentIllustration } from "../components/WorkMomentIllustration";
import { capabilityMapHref } from "../capabilityMapping";
import { assetHref } from "../routerMount";
import {
  businessModulePath,
  downloadAssetPath,
  groundworksEntityPath,
  integrationSystemPath,
  modulePathFromId,
  rolePath,
  workMomentPath,
} from "../catalogue";
import { usePageTitle, useRevealOnce } from "./support";

/**
 * Brief PDF filename contract, shared with tooling/generate-brief-pdfs.ts.
 *
 * A story's PDF companion is named after the story's first governed download
 * asset id — the same id the markdown brief is published under — with a
 * `.pdf` suffix, served from the volume mount at /downloads/briefs/. The
 * generator writes exactly this filename from the same asset id; neither side
 * may re-derive the name from the story id, which only happens to match
 * today. Change the contract here and in the generator together.
 */
export function briefPdfPath(briefAssetId: string): string {
  return `/downloads/briefs/${briefAssetId}.pdf`;
}

/**
 * A governed editorial story: a cross-domain decision journey rendered
 * entirely from the projected editorial-story record. The page duplicates no
 * contract; every module, flow, agent, record, system and Work Moment it
 * mentions is a link to that object's canonical route, and every visible
 * sentence comes from the story's locale keys.
 *
 * copy-register: commercial
 */
export function EditorialStoryPage({ storyId }: { storyId: string }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const story = snapshot.content.editorialStories?.find(
    (candidate) => candidate.id === storyId,
  );
  usePageTitle(story ? copy(story.titleKey) : undefined, Boolean(story));
  const reveal = useRevealOnce<HTMLOListElement>();
  const briefAssetId = story?.resourceIds?.[0];
  /*
   * The PDF companion lives on the deployment volume, not in the build: a
   * deployment without the mount has no PDF to offer. The link is therefore
   * published only once the file answers for itself. A HEAD probe that both
   * succeeds and returns a PDF content type is the whole test, so a service
   * that answers the path with the application shell never reaches a reader
   * as a download.
   */
  const [briefPdfPublished, setBriefPdfPublished] = useState(false);
  useEffect(() => {
    if (!briefAssetId || typeof fetch !== "function") return;
    const controller = new AbortController();
    let current = true;
    fetch(assetHref(briefPdfPath(briefAssetId)), {
      method: "HEAD",
      signal: controller.signal,
    })
      .then((response) => {
        const contentType = response.headers.get("content-type") ?? "";
        if (current && response.ok && contentType.includes("application/pdf")) {
          setBriefPdfPublished(true);
        }
      })
      .catch(() => {
        /* No mount, no network, no link: the absence is the answer. */
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [briefAssetId]);
  if (!story) {
    return (
      <div className="apf-page apf-register-marketing">
        <EmptyState
          title={copy("editorial-story.missing.title", "This story is not published for this audience.")}
          description={copy("editorial-story.missing.body", "The governed story record is not part of this projection.")}
        />
      </div>
    );
  }

  const moduleById = new Map(
    snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]),
  );
  const entityById = new Map(
    snapshot.groundworks.entities.map((entity) => [entity.id, entity]),
  );
  const roleById = new Map(
    (snapshot.content.roles ?? []).map((role) => [role.id, role]),
  );
  const systems = story.systemProfileIds.flatMap((systemId) => {
    const system = snapshot.integrations.systemProfiles.find(({ id }) => id === systemId);
    return system ? [system] : [];
  });
  const flowLink = (flowId: string) => {
    for (const moduleId of story.businessModuleIds) {
      const module = moduleById.get(moduleId);
      const flow = module?.executionFlows.find(({ id }) => id === flowId);
      if (module && flow) {
        return {
          id: flowId,
          label: flow.label,
          to: `${businessModulePath(module.domainWorkblockId, module.id)}#flow-${flowId}`,
        };
      }
    }
    return { id: flowId, label: humanize(flowId), to: undefined };
  };
  const roleLink = (roleId: string) => {
    const role = roleById.get(roleId);
    return {
      label: role ? copy(role.labelKey) : humanize(roleId),
      to: rolePath(roleId),
    };
  };

  return (
    <div className="apf-page apf-register-marketing apf-editorial-story">
      <PlaceTrail
        stops={[
          { label: "Framework", to: "/" },
          { label: copy("nav.client-engagement", "Client engagement"), to: "/client-engagement" },
          { label: copy(story.titleKey) },
        ]}
      />
      <div className="apf-scene-hero">
        <PageHeader
          eyebrow={copy(story.kickerKey)}
          title={copy(story.titleKey)}
          description={copy(story.summaryKey)}
        />
      </div>

      <section
        className="apf-page-section apf-editorial-story__journey"
        aria-labelledby="story-journey-title"
      >
        <SectionHeading
          id="story-journey-title"
          title={copy("editorial-story.journey.title", "The decision journey")}
          description={copy(
            `editorial-story.${story.id}.journey.description`,
            "Each chapter has a named owner, governed records, an explicit authority boundary and a handoff the next owner can hold it to.",
          )}
        />
        <ol className={`apf-editorial-story__chapters${reveal.revealClass}`} ref={reveal.ref}>
          {story.chapters.map((chapter, index) => {
            const owner = roleLink(chapter.ownerRoleId);
            return (
              <li key={chapter.id} className="apf-editorial-story__chapter" id={`chapter-${chapter.id}`}>
                <header className="apf-editorial-story__chapter-head">
                  <span className="apf-editorial-story__ordinal">{ordinal(index + 1)}</span>
                  <h3>{copy(chapter.titleKey)}</h3>
                </header>
                <p className="apf-editorial-story__chapter-body">{copy(chapter.bodyKey)}</p>
                <dl className="apf-editorial-story__chapter-facts">
                  <div>
                    <dt>{copy("editorial-story.chapter.owner", "Owner")}</dt>
                    <dd>
                      <Link to={owner.to}>{owner.label}</Link>
                    </dd>
                  </div>
                  <div>
                    <dt>{copy("editorial-story.chapter.records", "Records")}</dt>
                    <dd className="apf-inline-link-list">
                      {chapter.recordEntityIds.map((entityId, itemIndex) => {
                        const entity = entityById.get(entityId);
                        return (
                          <span key={entityId}>
                            {itemIndex ? " / " : null}
                            {entity ? (
                              <Link to={groundworksEntityPath(entityId)}>{entity.label}</Link>
                            ) : (
                              humanize(entityId)
                            )}
                          </span>
                        );
                      })}
                    </dd>
                  </div>
                  <div>
                    <dt>{copy("editorial-story.chapter.flows", "Execution flows")}</dt>
                    <dd className="apf-inline-link-list">
                      {chapter.executionFlowIds.map((flowId, itemIndex) => {
                        const flow = flowLink(flowId);
                        return (
                          <span key={flowId}>
                            {itemIndex ? " / " : null}
                            {flow.to ? <Link to={flow.to}>{flow.label}</Link> : flow.label}
                          </span>
                        );
                      })}
                    </dd>
                  </div>
                  <div>
                    <dt>{copy("editorial-story.chapter.authority", "Authority")}</dt>
                    <dd>{copy(chapter.authorityKey)}</dd>
                  </div>
                  <div>
                    <dt>{copy("editorial-story.chapter.handoff", "Handoff")}</dt>
                    <dd>{copy(chapter.handoffKey)}</dd>
                  </div>
                </dl>
              </li>
            );
          })}
        </ol>
      </section>

      <section
        className="apf-page-section apf-band apf-band--tint apf-editorial-story__moment"
        aria-labelledby="story-moment-title"
      >
        <p className="apf-proof-line">
          <span>{copy("work-moment.proof.illustrative", "Illustrative work moment")}</span>
          <span>{copy("work-moment.proof.synthetic", "Synthetic data")}</span>
        </p>
        <SectionHeading
          id="story-moment-title"
          title={copy("editorial-story.moment.title", "One moment, illustrated")}
          description={copy(
            `editorial-story.${story.id}.moment.description`,
            "This is where the journey turns from evidence into a bounded decision. The proposal and the human commitment stay visibly distinct.",
          )}
        />
        <WorkMomentIllustration momentId={story.workMomentId} variant="compact" />
        <p className="apf-deep-link">
          <Link to={workMomentPath(story.workMomentId)}>
            <ArrowLinkLabel>
              {copy("work-moment.chapter.open-record", "Open the complete Work moment record")}
            </ArrowLinkLabel>
          </Link>
        </p>
      </section>

      <section
        className="apf-page-section apf-editorial-story__systems"
        aria-labelledby="story-systems-title"
      >
        <SectionHeading
          id="story-systems-title"
          title={copy("editorial-story.systems.title", "The system-of-record boundary")}
        />
        <p className="apf-editorial-story__boundary">{copy(story.systemBoundaryKey)}</p>
        <ul className="apf-editorial-story__system-list">
          {systems.map((system) => (
            <li key={system.id}>
              <Link to={integrationSystemPath(system.id)}>{system.name}</Link>
              <p>{system.designQuestion}</p>
            </li>
          ))}
        </ul>
      </section>

      <section
        className="apf-page-section apf-editorial-story__map"
        aria-labelledby="story-map-title"
      >
        <SectionHeading
          id="story-map-title"
          title={copy("editorial-story.map.title", "Where the capabilities land")}
          description={copy(
            `editorial-story.${story.id}.map.description`,
            "Each capability maps onto an existing business module of the foundation. Nothing new is invented for the story.",
          )}
        />
        <p className="apf-deep-link">
          <Link to={capabilityMapHref({ view: "partner", mapId: story.capabilityMapId })}>
            <ArrowLinkLabel>
              {copy(`editorial-story.${story.id}.map.open`, "Open the capability map")}
            </ArrowLinkLabel>
          </Link>
        </p>
      </section>

      <section
        className="apf-page-section apf-editorial-story__outcome"
        aria-labelledby="story-outcome-title"
      >
        <SectionHeading
          id="story-outcome-title"
          title={copy("editorial-story.outcome.title", "What the journey leaves behind")}
        />
        <p className="apf-editorial-story__outcome-body">{copy(story.outcomeKey)}</p>
        <ul className="apf-editorial-story__module-list">
          {story.businessModuleIds.map((moduleId) => {
            const module = moduleById.get(moduleId);
            const path = modulePathFromId(snapshot, moduleId);
            if (!module || !path) return null;
            return (
              <li key={moduleId}>
                <Link to={path}>{copy(module.labelKey)}</Link>
                <p>{copy(module.jobStatementKey)}</p>
              </li>
            );
          })}
        </ul>
        {briefAssetId ? (
          <>
            <p className="apf-deep-link">
              <Link to={downloadAssetPath(briefAssetId)}>
                <ArrowLinkLabel>
                  {copy(`editorial-story.${story.id}.brief.open`, "Download the framework brief")}
                </ArrowLinkLabel>
              </Link>
            </p>
            {briefPdfPublished ? (
              <p className="apf-deep-link">
                <a href={assetHref(briefPdfPath(briefAssetId))} download>
                  <ArrowLinkLabel>
                    {copy(`editorial-story.${story.id}.brief.pdf`, "Download the brief as PDF")}
                  </ArrowLinkLabel>
                </a>
              </p>
            ) : null}
          </>
        ) : null}
      </section>
    </div>
  );
}
